import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ChevronLeft, ChevronRight, Download, Loader } from "react-feather";

import Modal from "../../../Modal2/Modal";
import FileIcon from "../../../../components/ctvoicer/fileIcon/FileIcon";

import { getFileSize } from "../../../../utils/fileAttaches";
import { getInternalAttachmentUrl, getInternalGroupAttachmentUrl } from "../../../../utils/internalAttachments";

import { FileFormatIcon } from "./InternalFilePreview";
import { downloadAttachment } from "./downloadAttachment";

import "./internal-attachments.scss";

/** Visualizador de anexos do chat interno.
 *  `viewer` = { attachments, messageId, index, isGroup } ou null.
 *
 *  O `ImageGallery` do chat de clientes não serve aqui: ele está envolto num
 *  <AccessControl allowedPermissions={["attach.files"]}>, ou seja, quem não pode
 *  ANEXAR também não conseguiria VISUALIZAR. O que dá para reaproveitar é o `FileIcon`,
 *  que renderiza imagem, PDF, vídeo e áudio a partir de `file.url` — e só dele.
 */
const InternalAttachmentsViewer = ({ viewer, onClose }) => {
    const intl = useIntl();

    const [current, setCurrent] = useState(0);
    const [loadingDownload, setLoadingDownload] = useState(false);

    const attachments = (viewer && viewer.attachments) || [];
    const messageId = viewer ? viewer.messageId : null;
    const isGroup = !!(viewer && viewer.isGroup);
    const currentAttachment = attachments[current] || null;

    const getAttachmentUrl = useCallback((attachment) => {
        return isGroup ? getInternalGroupAttachmentUrl(attachment) : getInternalAttachmentUrl(attachment, messageId);
    }, [isGroup, messageId]);

    useEffect(() => {
        setCurrent(viewer ? viewer.index : 0);
    }, [viewer]);

    const handleDownload = useCallback(async () => {
        if (!currentAttachment) return;
        const url = getAttachmentUrl(currentAttachment);
        if (!url) return;

        setLoadingDownload(true);
        try {
            await downloadAttachment(url, currentAttachment.fileName);
        } catch (error) {
            console.error('Erro ao baixar o anexo:', error);
        } finally {
            setLoadingDownload(false);
        }
    }, [currentAttachment, getAttachmentUrl]);

    /** O FileIcon acessa `file.fileName.split` e `file.contentType.split` sem guarda —
     *  um anexo sem contentType derrubaria o visualizador. */
    const toFileIconData = (attachment) => ({
        url: getAttachmentUrl(attachment),
        fileName: attachment.fileName || '',
        contentType: attachment.contentType || 'application/octet-stream'
    });

    /** O Modal registra o listener de teclado no `componentDidUpdate`, só quando `show`
     *  MUDA. Montar já com `show` fixo em true nunca dispara essa transição e o Esc não
     *  fecharia. Por isso o Modal fica sempre montado (o SmartRendering dele não pinta
     *  nada até a primeira abertura) e quem alterna é o `show`.
     */
    return (
        <Modal
            show={!!viewer && attachments.length > 0}
            toggle={onClose}
            position="center"
            size="xl"
            className="internal-attachments-viewer-ui"
            onPressEnter={() => {}}
        >
            <Modal.Header>
                <div className="internal-attachments-viewer-header">
                    <span className="internal-attachments-viewer-name">
                        {currentAttachment && currentAttachment.fileName}
                    </span>
                    <span className="internal-attachments-viewer-meta">
                        {currentAttachment && getFileSize(currentAttachment.sizeBytes || 0)}
                    </span>
                    <span role="button" className="internal-attachments-viewer-download" onClick={handleDownload}>
                        {loadingDownload ? <Loader size={16} className="internal-attachment-spin" /> : <Download size={16} />}
                        {intl.formatMessage({ id: "Baixar" })}
                    </span>
                </div>
            </Modal.Header>

            <Modal.Content>
                <div className="internal-attachments-viewer-body">
                    {attachments.length > 1 && (
                        <span
                            role="button"
                            className={`internal-attachments-viewer-nav ${current === 0 ? 'disabled' : ''}`}
                            onClick={() => setCurrent(prev => Math.max(prev - 1, 0))}
                        >
                            <ChevronLeft size={28} />
                        </span>
                    )}

                    <div className="internal-attachments-viewer-stage">
                        {currentAttachment && (
                            <FileIcon key={currentAttachment.storageName} file={toFileIconData(currentAttachment)} />
                        )}
                    </div>

                    {attachments.length > 1 && (
                        <span
                            role="button"
                            className={`internal-attachments-viewer-nav ${current === attachments.length - 1 ? 'disabled' : ''}`}
                            onClick={() => setCurrent(prev => Math.min(prev + 1, attachments.length - 1))}
                        >
                            <ChevronRight size={28} />
                        </span>
                    )}
                </div>
            </Modal.Content>

            {attachments.length > 1 && (
                <Modal.Footer>
                    <div className="internal-attachments-viewer-thumbnails">
                        {attachments.map((attachment, index) => {
                            const url = getAttachmentUrl(attachment);
                            const isImage = (attachment.contentType || '').startsWith('image/');
                            return (
                                <div
                                    key={`viewer-thumb-${index}`}
                                    className={`internal-attachments-viewer-thumbnail ${index === current ? 'active' : ''}`}
                                    onClick={() => setCurrent(index)}
                                    title={attachment.fileName}
                                >
                                    {/* Miniatura própria em vez de FileIcon: ele baixa o blob inteiro de
                                        PDF e vídeo dentro de um useEffect, o que baixaria todos os anexos
                                        da mensagem só para desenhar a tira. */}
                                    {isImage && url
                                        ? <img src={url} alt={attachment.fileName} />
                                        : <FileFormatIcon fileName={attachment.fileName} className="internal-attachments-viewer-thumbnail-icon" />
                                    }
                                </div>
                            );
                        })}
                    </div>
                </Modal.Footer>
            )}
        </Modal>
    );
}

export default InternalAttachmentsViewer;
